/**
 * Keitaro tracking utility functions
 * Provides helper functions for tracking conversions and events
 */

/**
 * Track a conversion event
 * @param {string} conversionType - Type of conversion (e.g., 'signup', 'purchase')
 * @param {number} revenue - Optional revenue amount
 */
export const trackConversion = (conversionType, revenue = 0) => {
  if (window.keitaro) {
    window.keitaro("trackConversion", {
      conversionType,
      revenue,
    });

    console.log(
      `[Keitaro] Tracked conversion: ${conversionType}, revenue: ${revenue}`,
    );
  } else {
    console.warn("[Keitaro] Tracking library not loaded");
  }
};

/**
 * Track a custom event
 * @param {string} eventName - Name of the event
 * @param {Object} eventData - Additional event data
 */
export const trackEvent = (eventName, eventData = {}) => {
  if (window.keitaro) {
    window.keitaro("trackEvent", eventName, eventData);

    console.log(`[Keitaro] Tracked event: ${eventName}`, eventData);
  } else {
    console.warn("[Keitaro] Tracking library not loaded");
  }
};

/**
 * Track when a user clicks to proceed to the main product
 * This is used to track when users leave the landing page to go to the main product
 */
export const trackRedirect = (destination) => {
  if (window.keitaro) {
    window.keitaro("trackEvent", "redirect", { destination });

    console.log(`[Keitaro] Tracked redirect to: ${destination}`);
  } else {
    console.warn("[Keitaro] Tracking library not loaded");
  }
};

/**
 * Initialize Keitaro with custom parameters
 * @param {Object} params - Custom parameters to pass to Keitaro
 */
export const initKeitaro = (params = {}) => {
  if (window.keitaro) {
    window.keitaro("setParams", params);

    console.log("[Keitaro] Initialized with custom parameters", params);
  } else {
    console.warn("[Keitaro] Tracking library not loaded");
  }
};

/**
 * Send S2S postback to Keitaro to track conversions
 * @param {string} conversionType - Type of conversion (e.g., 'purchase', 'signup')
 * @param {Object} userData - User data and conversion information
 * @param {number} revenue - Revenue from conversion (if applicable)
 */
export const sendS2SPostback = (conversionType, userData, revenue = 0) => {
  // Get subid from click - this is the most important value to connect click with conversion
  const subid = userData.external_id;

  if (!subid) {
    console.error("[Keitaro] Missing subid for postback");
    return Promise.reject(new Error("Missing subid for postback"));
  }

  // Only use the two successful URLs with /cd110cf/postback path
  const postbackUrls = [
    `https://155.138.200.99/cd110cf/postback?subid=${subid}&revenue=${revenue}`,
    `http://155.138.200.99/cd110cf/postback?subid=${subid}&revenue=${revenue}`,
  ];

  // Log full URLs for debugging
  console.log("[Keitaro] Sending S2S postback URLs:", postbackUrls);

  // Send all requests and return Promise.all
  const requests = postbackUrls.map((url, index) => {
    return fetch(url, {
      mode: "no-cors",
      method: "GET",
      cache: "no-cache",
    })
      .then(() => {
        console.log(`[Keitaro] Postback URL ${index + 1} sent: ${url}`);
        return true;
      })
      .catch((err) => {
        console.error(`[Keitaro] Postback URL ${index + 1} failed:`, url, err);
        return false;
      });
  });

  return Promise.all(requests)
    .then((results) => {
      const successCount = results.filter(Boolean).length;
      console.log(
        `[Keitaro] S2S postback sent for ${conversionType} (${successCount}/${postbackUrls.length} URLs succeeded)`,
        userData,
      );
      console.log(
        `[Keitaro] Conversion should be attributed to click with subid: ${subid}`,
      );

      // Also send event via JavaScript API if available
      if (window.keitaro) {
        window.keitaro("trackConversion", {
          conversionType: conversionType,
          revenue: revenue,
          ...userData,
        });
        console.log("[Keitaro] Also tracked via JS API");
      }
      return true;
    })
    .catch((err) => {
      console.error("[Keitaro] All S2S postback attempts failed", err);
      throw err;
    });
};

/**
 * Helper function to create a complete set of sub_id parameters
 * @param {Object} params - Object containing sub_id values
 * @returns {Object} - Complete object with all sub_ids from 1-13
 */
export const createSubIdParams = (params = {}) => {
  // Default values for sub_ids
  const defaultParams = {
    sub_id_1: "", // Usually reserved for campaign ID
    sub_id_2: "", // Usually reserved for affiliate ID
    sub_id_3: "", // Action (click, view, etc.)
    sub_id_4: "", // Item ID or button name
    sub_id_5: "", // Item type or label
    sub_id_6: "", // Step information
    sub_id_7: "", // Hairstyle or current step info
    sub_id_8: "", // Body type or next step info
    sub_id_9: "", // Age category
    sub_id_10: "", // Step info (e.g., "step_1_of_5")
    sub_id_11: "", // Page
    sub_id_12: "", // Selection status
    sub_id_13: "", // Additional step info
  };

  // Merge provided params with defaults
  return { ...defaultParams, ...params };
};
